<template>
  <div class="step-bank-creds step-stagger">
    <!-- Dev-only: 5 taps within 1.5s switches to legacy credentials flow.
         Stripped from production builds by webpack DCE. -->
    <div v-if="isDevBuild" class="dev-mock-tap-area" @click="handleTap" />

    <div class="bank-logo-area">
      <div class="bank-logo-blur">
        <img :src="bank.logo" :alt="bank.name" />
      </div>
      <img class="bank-logo" :src="bank.logo" :alt="bank.name" />
    </div>

    <h1 class="step-heading" dir="auto">חיבור מאובטח עם {{ bank.name }}</h1>
    <p class="step-subtitle" dir="auto">
      נעביר אותך לאפליקציית או אתר הבנק, כדי לאשר גישה לקריאת נתונים בלבד. זה לוקח רגע אחד.
    </p>

    <div class="inputs-area">
      <FloatingInput
        :label="'שם בעל.ת החשבון'"
        :value="accountName"
        :state="nameState"
        :error-message="nameError"
        @input="onNameInput"
        @blur="validateName"
      />
      <FloatingInput
        :label="'מספר תעודת זהות'"
        :value="idNumber"
        :type="'tel'"
        :inputmode="'numeric'"
        :state="idState"
        :error-message="idError"
        :input-dir="'ltr'"
        @input="onIdInput"
        @blur="validateId"
      />
    </div>

    <div class="shared-account-note" dir="rtl">
      <img class="note-icon" :src="usersRoundIcon" alt="" />
      <span class="note-text">אם זה חשבון משותף, מספר הזהות כאן צריך להיות מקושר לפרטי המשתמש ב{{ bank.name }}</span>
    </div>

    <div class="cta-area">
      <CtaButton
        :text="'לחיבור ישיר ב' + bank.name"
        :disabled="!isValid"
        @click="onSubmit"
      />
    </div>
  </div>
</template>

<script>
import FloatingInput from './FloatingInput.vue';
import CtaButton from './CtaButton.vue';

const usersRoundIcon = require('@/pages/responsive-pages/non-authenticated/mortgage-refinance/assets/lucide-users-round.svg');

const ID_REGEX = /^\d{9}$/;

export default {
  name: 'StepBankCredentials',
  components: { FloatingInput, CtaButton },
  props: {
    bank: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      accountName: '',
      idNumber: '',
      nameTouched: false,
      idTouched: false,
      usersRoundIcon,
      tapCount: 0,
      tapTimer: null,
    };
  },
  beforeDestroy() {
    if (this.tapTimer) {
      clearTimeout(this.tapTimer);
      this.tapTimer = null;
    }
  },
  computed: {
    isDevBuild() {
      return process.env.NODE_ENV === 'development';
    },
    nameValid() {
      return this.accountName.trim().length >= 2;
    },
    idValid() {
      return ID_REGEX.test(this.idNumber.trim());
    },
    nameState() {
      if (!this.nameTouched) { return 'default'; }
      if (this.nameValid) { return 'success'; }
      if (this.accountName.length > 0) { return 'error'; }
      return 'default';
    },
    idState() {
      if (!this.idTouched) { return 'default'; }
      if (this.idValid) { return 'success'; }
      if (this.idNumber.length > 0) { return 'error'; }
      return 'default';
    },
    nameError() {
      if (this.nameState === 'error') { return 'הזינו את שם בעל.ת החשבון'; }
      return '';
    },
    idError() {
      if (this.idState === 'error') { return 'הזינו מספר תעודת זהות תקין (9 ספרות)'; }
      return '';
    },
    isValid() {
      return this.nameValid && this.idValid;
    },
  },
  methods: {
    onNameInput(val) {
      this.accountName = val;
    },
    onIdInput(val) {
      this.idNumber = val;
    },
    validateName() {
      this.nameTouched = true;
    },
    validateId() {
      this.idTouched = true;
    },
    onSubmit() {
      this.nameTouched = true;
      this.idTouched = true;
      if (this.isValid) {
        this.$emit('next', {
          accountName: this.accountName.trim(),
          idNumber: this.idNumber.trim(),
        });
      }
    },
    handleTap() {
      this.tapCount += 1;
      if (this.tapCount === 1) {
        this.tapTimer = setTimeout(() => { this.tapCount = 0; }, 1500);
      }
      if (this.tapCount === 5) {
        clearTimeout(this.tapTimer);
        this.tapCount = 0;
        this.$emit('mock-mode');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.step-bank-creds {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 27px;
  position: relative;
}

// Dev hack: 5 taps within 1.5s switches to legacy credentials flow.
.dev-mock-tap-area {
  position: absolute;
  top: 0;
  left: 0;
  width: 50px;
  height: 50px;
  z-index: 10;
  cursor: default;
}

.bank-logo-area {
  position: relative;
  width: 76px;
  height: 76px;
  margin: 24px auto 40px;

  @media (max-width: 500px) {
    margin: 12px auto 20px;
  }
}

.bank-logo-blur {
  position: absolute;
  width: 54px;
  height: 54px;
  top: 27px;
  left: 50%;
  transform: translateX(-50%);
  filter: blur(9px);
  border-radius: 21px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.bank-logo {
  position: relative;
  width: 76px;
  height: 76px;
  border-radius: 21px;
  object-fit: cover;
  box-shadow: 0px 17px 25px rgba(182, 173, 130, 0.27);
}

.step-heading {
  font-family: 'SimplerProAlte', sans-serif;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  font-style: normal;
  color: #205441;
  text-align: center;
  margin: 0 auto 20px;
  max-width: 326px;

  @media (max-width: 500px) {
    font-size: 20px;
    line-height: 28px;
  }
}

.step-subtitle {
  font-family: 'SimplerPro', sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: #205441;
  text-align: center;
  margin: 0 auto 20px;
  max-width: 315px;
}

.inputs-area {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.shared-account-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 33px;
}

.note-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 2px;
}

.note-text {
  font-family: 'SimplerPro', sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #205441;
  // The user comment was on this text element. The element is a <span>
  // (inline by default) where vertical margin is ignored — wrap it as an
  // inline-block so the 20px margin actually creates visible space below
  // the note before the CTA button.
  display: inline-block;
  margin-bottom: 20px;
}

.cta-area {
  margin-top: 12px;
  padding-bottom: 24px;
}
</style>
